/**
 * Assessment data for the team assessments page
 * Organized by category and subcategory for easy maintenance
 */

export interface AssessmentSubcategory {
  title: string
  items: string[]
  description?: string
}

export interface AssessmentCategory {
  id: string
  title: string
  icon: 'brain' | 'book' | 'puzzle'
  description: string
  subcategories: AssessmentSubcategory[]
}

export const assessmentCategories: AssessmentCategory[] = [
  {
    id: `psychoeducational`,
    title: `Psychoeducational Assessment`,
    icon: `brain`,
    description: `Comprehensive evaluation of cognitive and social functioning`,
    subcategories: [
      {
        title: `Intelligence Testing`,
        items: [
          `Wechsler's Preschool and Primary Scale of Intelligence`,
          `Wechsler's Intelligence Scale for Children`,
          `Wechsler's Adult Intelligence Scale of Specific Learning Disability Testing`,
          `Wechsler's Individual Achievement Test‑4`,
          `Wechsler's Intelligence Scale for Children (WISC)`,
          `Wechsler's Adult Intelligence Scale (WAIS)`,
          `Seguin Form Board Test (SFB)`,
          `Binet‑Kamat Test (BKT)`,
        ],
        description: `Standardized tests to measure cognitive abilities and intellectual functioning`,
      },
      {
        title: `Social Assessment`,
        items: [
          `Vineland Social Maturity Screening`,
          `Behaviour Assessment System for Children‑3`,
        ],
        description: `Evaluation of social skills and adaptive behavior`,
      },
    ],
  },
  {
    id: `learning-memory`,
    title: `Learning & Memory`,
    icon: `book`,
    description: `Assessment of learning capabilities and memory function`,
    subcategories: [
      {
        title: `Learning Disability`,
        items: [`NIMHANS Specific Learning Disability Testing`],
        description: `Specialized testing for specific learning disorders`,
      },
      {
        title: `Memory Assessment`,
        items: [
          `Mini Mental Status Examination (MMSE)`,
          `Addenbrooke's Cognitive Examination (ACE)`,
          `Post Graduate Institute Battery of Brain Dysfunction (PGI Battery)`,
        ],
        description: `Comprehensive evaluation of memory and cognitive function`,
      },
    ],
  },
  {
    id: `other`,
    title: `Other Assessments`,
    icon: `puzzle`,
    description: `Specialized evaluations for various mental health and developmental concerns`,
    subcategories: [
      {
        title: `Comprehensive Evaluations`,
        items: [
          `Autism Screening – Indian Scale for Assessment of Autism`,
          `Developmental Screening – Ages and Stages Questionnaire`,
          `Surrogacy Evaluations`,
          `ADHD Evaluations for Children`,
          `Adolescents & Adults: Connor's ADHD Evaluation`,
          `Amen's Multidimensional Evaluation of Neurodevelopmental Disorder Scale for ADHD`,
          `Central Nervous System Vitals (CNSVS)`,
          `Mood‑related assessments for children, adolescents and adults`,
        ],
        description: `Wide range of specialized assessments for various conditions`,
      },
    ],
  },
]

/**
 * Get assessment category by ID
 */
export function getAssessmentCategory(
  id: string,
): AssessmentCategory | undefined {
  return assessmentCategories.find((cat) => cat.id === id)
}

/**
 * Get all assessment items as a flat array
 */
export function getAllAssessments(): string[] {
  return assessmentCategories.flatMap((category) =>
    category.subcategories.flatMap((sub) => sub.items),
  )
}

/**
 * Get total count of all assessments
 */
export function getTotalAssessmentCount(): number {
  return getAllAssessments().length
}

/**
 * Search assessments by keyword
 */
export function searchAssessments(keyword: string): string[] {
  const lowerKeyword = keyword.toLowerCase()
  return getAllAssessments().filter((item) =>
    item.toLowerCase().includes(lowerKeyword),
  )
}
